import { useState, useEffect } from "react";
import "../../assets/scss/feature-types/add-feature-type.scss";
import axiosConfig from "../../services/axiosConfig";
import setHeader from "../../components/Functions/setHeader";
import SweetAlert from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
// Import reusable components
import SearchableDropdownInput from "../../components/theme/SearchableDropdownInput";
import TextInput from "../../components/theme/TextInput";
import TextAreaInput from "../../components/theme/TextAreaInput";
import FileInput from "../../components/theme/FileInput";
import DateTimeInput from "../../components/theme/DateTimeInput";

const AddHomeSection = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const [typeData, setTypeData] = useState([]);
    const [productData, setProductData] = useState([]);
    const user_id = localStorage.getItem("userId");
    const navigate = useNavigate()

    const [formData, setFormData] = useState({
        text: "",
        description: "",
        type: "",
        expire_date: "",
        image: null, // Changed to null for file handling
    });

    const types = [
        { t_id: 1, name: "Home Square Card Section 3" },
        // { t_id: 2, name: "Flash Sale" },
        { t_id: 2, name: "Hot Deals" },
        { t_id: 3, name: "Best Sells" },
        { t_id: 4, name: "Footer Banner" },
    ];

    const [isLoading, setIsLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [filteredTypes, setFilteredTypes] = useState([]);
    const [typeRestrictions, setTypeRestrictions] = useState({});

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        setLoading(true);
        setData([]); // Reset data before fetching new data

        axiosConfig.get("home-section-list", setHeader())
            .then((res) => {
                setData(res.data.data);
                console.log(res.data.data);
                
                // Filter types based on existing data
                filterAvailableTypes(res.data.data);
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            })
            .finally(() => {
                setLoading(false); // ✅ This will run after the promise resolves/rejects
            });
    };

    const filterAvailableTypes = (existingData) => {
        if (isEditMode) {
            // In edit mode, allow all types including the current one
            setFilteredTypes(types);
            return;
        }

        // Count existing types
        const typeCounts = {};
        existingData.forEach(item => {
            const type = item.type;
            typeCounts[type] = (typeCounts[type] || 0) + 1;
        });

        // Define type limits
        const typeLimits = {
            "Home Square Card Section 3": 4, // Allow up to 4
            "Hot Deals": 1, // Allow only 1
            "Best Sells": 1, // Allow only 1
        };

        // Filter available types and create restriction messages
        const availableTypes = [];
        const restrictions = {};

        types.forEach(type => {
            const currentCount = typeCounts[type.name] || 0;
            const limit = typeLimits[type.name] || 1;

            if (currentCount < limit) {
                availableTypes.push(type);
            } else {
                // Create restriction message
                if (limit === 1) {
                    restrictions[type.name] = `Cannot add more than one ${type.name} section`;
                } else {
                    restrictions[type.name] = `Cannot add more than ${limit} ${type.name} sections (${currentCount}/${limit} used)`;
                }
            }
        });

        setFilteredTypes(availableTypes);
        setTypeRestrictions(restrictions);
    };

    useEffect(() => {
        if (isEditMode) {
            fetchHomeSectionData();
        }
    }, [id, isEditMode]);

    const fetchHomeSectionData = async () => {
        setIsDataLoading(true);
        try {
            const response = await axiosConfig.get(`get-home-section/${id}`, setHeader());
            if (!response.data.error) {
                const homeSectionData = response.data.data || response.data;

                // Handle both array and single object responses
                const data = Array.isArray(homeSectionData) ? homeSectionData[0] : homeSectionData;

                const newFormData = {
                    text: data.text || "",
                    description: data.description || "",
                    type: data.type || "",
                    expire_date: data.expire_date ? formatDateTimeForInput(data.expire_date) : "",
                    image: null, // Don't set existing image file, just keep reference
                };

                setFormData(newFormData);
            } else {
                SweetAlert.fire({
                    title: "Error",
                    text: "Failed to fetch home section data",
                    icon: "error",
                });
            }
        } catch (error) {
            console.error('Error fetching home section data:', error);
            SweetAlert.fire({
                title: "Error",
                text: "An error occurred while fetching home section data",
                icon: "error",
            });
        } finally {
            setIsDataLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            const maxSize = 400 * 1024; // 300KB in bytes

            if (file.size > maxSize) {
                SweetAlert.fire({
                    title: "Validation Error",
                    text: "Selected File is Larger than 400KB",
                    icon: "warning",
                });
                return;
            }

            setFormData((prev) => ({
                ...prev,
                image: file,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                image: null,
            }));
        }
    };

    const formatDateTimeForInput = (dateTimeString) => {
        if (!dateTimeString) return "";

        // Convert "2025-01-02 07:17:54" to "2025-01-02T07:17"
        const [datePart, timePart] = dateTimeString.split(' ');
        if (!datePart || !timePart) return "";

        const [hours, minutes] = timePart.split(':');
        return `${datePart}T${hours}:${minutes}`;
    };

    const formatDateTimeForAPI = (dateTimeString) => {
        if (!dateTimeString) return "";

        // Convert "2025-01-02T07:17" to "2025-01-02 07:17:54"
        const date = new Date(dateTimeString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.text) {
            SweetAlert.fire({
                title: "Validation Error",
                text: "Text is required",
                icon: "warning",
            });
            return;
        }

        if (!formData.type) {
            SweetAlert.fire({
                title: "Validation Error",
                text: "Type selection is required",
                icon: "warning",
            });
            return;
        }

        // Image validation for add mode
        if (!isEditMode && !formData.image) {
            SweetAlert.fire({
                title: "Validation Error",
                text: "Image is required",
                icon: "warning",
            });
            return;
        }

        setIsLoading(true);

        try {
            // Create FormData for file upload
            const submitData = new FormData();

            submitData.append('text', formData.text);
            submitData.append('description', formData.description || '');
            submitData.append('type', formData.type);
            submitData.append('expire_date', formData.expire_date ? formatDateTimeForAPI(formData.expire_date) : '');

            // Add image if selected
            if (formData.image) {
                submitData.append('image', formData.image);
            } else if (!isEditMode) {
                // For add mode without image, append empty string
                submitData.append('image', '');
            }

            // For edit mode, add the ID
            if (isEditMode) {
                submitData.append('h_id', id);
            }

            const apiEndpoint = isEditMode ? "update-home-section" : "save-home-section";
            const successMessage = isEditMode
                ? "Home section updated successfully"
                : "Home section added successfully";

            const response = await axiosConfig.post(apiEndpoint, submitData, {
                ...setHeader(),
                'Content-Type': 'multipart/form-data',
            });

            setIsLoading(false);

            if (!response.data.error) {
                SweetAlert.fire({
                    title: "Success",
                    text: response.data.messages || successMessage,
                    icon: "success",
                });
                navigate('/pages/home-section/home-section-list')
                // Reset form after successful submission (only for add mode)
                if (!isEditMode) {
                    handleReset();
                } else {
                    // For edit mode, refresh the data to show updated info
                    fetchHomeSectionData();
                }
            } else {
                SweetAlert.fire({
                    title: "Oops!",
                    text:
                        response.data.messages ||
                        `Failed to ${isEditMode ? "update" : "add"} home section. Try again!`,
                    icon: "error",
                });
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Backend error:', error);
            SweetAlert.fire({
                title: "Error",
                text: `An error occurred while ${isEditMode ? "updating" : "adding"} the home section`,
                icon: "error",
            });
        }
    };

    const handleReset = () => {
        if (isEditMode) {
            // For edit mode, reset to original data
            fetchHomeSectionData();
        } else {
            // For add mode, reset to empty form
            setFormData({
                text: "",
                description: "",
                type: "",
                expire_date: "",
                image: null,
            });
        }
    };

    if (isDataLoading || loading) {
        return (
            <div className="AddFeatureTypeMainWrapper">
                <div className="feature-type-form-container">
                    <div className="loading-state">Loading home section data...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="AddFeatureTypeMainWrapper">
            <div className="feature-type-form-container">
                <h2 className="form-title">
                    {isEditMode ? "Edit Home Section" : "Add Home Section"}
                </h2>

                {/* Show restrictions message if any types are restricted and not in edit mode */}
                {!isEditMode && Object.keys(typeRestrictions).length > 0 && (
                    <div className="type-restrictions-info" style={{ 
                        backgroundColor: '#fff3cd', 
                        border: '1px solid #ffeaa7', 
                        padding: '10px', 
                        marginBottom: '20px', 
                        borderRadius: '5px' 
                    }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#856404' }}>Type Restrictions:</h4>
                        {Object.entries(typeRestrictions).map(([type, message]) => (
                            <div key={type} style={{ color: '#856404', fontSize: '14px', marginBottom: '5px' }}>
                                • {message}
                            </div>
                        ))}
                    </div>
                )}

                {/* Show message when no types are available */}
                {!isEditMode && filteredTypes.length === 0 && (
                    <div className="no-types-available" style={{ 
                        backgroundColor: '#f8d7da', 
                        border: '1px solid #f5c6cb', 
                        padding: '15px', 
                        marginBottom: '20px', 
                        borderRadius: '5px',
                        textAlign: 'center'
                    }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#721c24' }}>No Types Available</h4>
                        <p style={{ margin: '0', color: '#721c24' }}>
                            All home section types have reached their maximum limit. Please delete existing sections to add new ones.
                        </p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="feature-type-form">
                    {/* Text input */}
                    <TextInput
                        id="text"
                        name="text"
                        label="Text"
                        type="text"
                        placeholder="Enter text"
                        value={formData.text}
                        onChange={handleInputChange}
                        required={true}
                    />

                    {/* Description
                    <TextAreaInput
                        id="description"
                        name="description"
                        label="Description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Enter description"
                        required={true}
                        rows={5}
                    /> */}

                    {/* Home Section Type Dropdown */}
                    <SearchableDropdownInput
                        id="type"
                        name="type"
                        label="Home Section Type"
                        value={formData.type}
                        onChange={handleInputChange}
                        options={filteredTypes.map(type => ({
                            value: type.name,
                            label: type.name
                        }))}
                        placeholder={filteredTypes.length === 0 ? "No types available" : "Select Type"}
                        required={true}
                        noOptionsMessage="No Type found"
                        disabled={!isEditMode && filteredTypes.length === 0}
                    />

                    {/* Expiry Date and Time */}
                    <DateTimeInput
                        id="expire_date"
                        name="expire_date"
                        label="Expire Date & Time"
                        value={formData.expire_date}
                        onChange={handleInputChange}
                        min={new Date().toISOString().slice(0, 16)} // Prevent past dates
                    />

                    {/* Image */}
                    <FileInput
                        id="image"
                        name="image"
                        label="Select Image"
                        onChange={handleFileChange}
                        accept="image/*"
                        required={!isEditMode} // Required only for add mode
                        placeholder="Choose image file..."
                    />

                    {/* Show current image name if in edit mode and file is selected */}
                    {formData.image && (
                        <div className="selected-file-info">
                            <small>Selected file: {formData.image.name}</small>
                        </div>
                    )}

                    {/* Form Actions */}
                    <div className="form-actions">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isLoading || (!isEditMode && filteredTypes.length === 0)}
                        >
                            {isLoading
                                ? `${isEditMode ? "Updating" : "Submiting..."}`
                                : `${isEditMode ? "Update" : "Submit"}`}
                        </button>
                        <button
                            type="button"
                            onClick={handleReset}
                            className="btn btn-secondary"
                            disabled={isLoading}
                        >
                            Reset
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddHomeSection;